import { cn } from "@stockpilot/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
}

/**
 * Stockpilot brand mark: isometric warehouse crate/container with a "verified
 * stock" checkmark. The mark lives ALWAYS inside a solid `bg-accent` chip, and
 * the entire illustration uses `accent-foreground` — a color pair whose contrast is
 * guaranteed by the theme engine across EVERY theme shipped by the app (light,
 * dark and all colored themes like Cyber Neon, Synthwave, Royal Velvet…).
 *
 * Gotcha (like GymLog): NEVER draw the mark with `text-accent`/`stroke-warning`
 * over a subtle surface — those tones disappear in some themes. The solid chip
 * + foreground is the only universally legible combination.
 */
function StockpilotMark(props: { class?: string }) {
  return (
    <span
      class={cn(
        "inline-flex items-center justify-center rounded-[28%] bg-accent text-accent-foreground shadow-sm",
        props.class,
      )}
    >
      <svg
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="h-[68%] w-[68%]"
        aria-hidden="true"
      >
        {/* Warehouse box / container (isometric) */}
        <path
          d="M9 12 16 8.5l7 3.5v8L16 23.5 9 20v-8Z"
          stroke="currentColor"
          stroke-width="1.9"
          stroke-linejoin="round"
          fill="none"
        />
        {/* Internal edges (volume of the box) */}
        <path
          d="M9 12l7 3.5 7-3.5M16 15.5v8"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linejoin="round"
          opacity="0.55"
        />
        {/* Verified stock checkmark */}
        <path
          d="m12.6 15 2.4 2.4 4.4-4.4"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <StockpilotMark class="h-8 w-8 shrink-0" />
          <span class="text-xl font-bold tracking-tight text-foreground">
            Stock<span class="text-accent">pilot</span>
          </span>
        </>
      ) : (
        <>
          <StockpilotMark class="mb-2 h-16 w-16 shrink-0 transition-transform duration-300 hover:scale-105 active:scale-95" />
          <h1 class="text-3xl font-bold tracking-tight text-foreground">
            Stock<span class="text-accent">pilot</span>
          </h1>
        </>
      )}
    </div>
  );
}
